// HistorySection.tsx - Simplified version (no need for user resolution in frontend)
import { useEffect, useState } from 'react';
import { User as UserIcon, ArrowRight, MessageCircle, Plus } from 'lucide-react'; // HIGHLIGHT: Added Plus icon
import { taskAPI } from '@/api/taskAPI';
import { Task } from '@/types';
import { AssigneeChange, Comment } from '@/types/comments';
import { ensureHtml, processHtmlForDisplay } from '@/utils/richText';

interface HistorySectionProps {
  taskId: string;
  comments: any[];
  task: any; // HIGHLIGHT: Added task prop
}

// HIGHLIGHT: Added 'task_create' type
interface TimelineItem {
  id: string;
  type: 'assignee_change' | 'comment' | 'task_create';
  data: AssigneeChange | Comment | Task;
  timestamp: string;
}

export const HistorySection: React.FC<HistorySectionProps> = ({ taskId, comments, task }) => {
  const [assigneeChanges, setAssigneeChanges] = useState<AssigneeChange[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([]);
  // HIGHLIGHT: Combined useEffect to process timeline items
  useEffect(() => {
    loadAssigneeChanges();
  }, []);
  useEffect(() => {
    loadAssigneeChanges();
  }, [taskId]);

  // HIGHLIGHT: Process and combine timeline items when data changes
  useEffect(() => {
    if (!isLoading && task) { // HIGHLIGHT: Added task condition
      const combinedItems: TimelineItem[] = [];
      
      // HIGHLIGHT: Add task creation as the first timeline item
      combinedItems.push({
        id: task.id,
        type: 'task_create',
        data: task,
        timestamp: task.created_at
      });
      
      // Add assignee changes to timeline
      assigneeChanges.forEach(change => {
        combinedItems.push({
          id: change.id,
          type: 'assignee_change',
          data: change,
          timestamp: change.changed_at
        });
      });
      
      // Add comments to timeline
      comments.forEach(comment => {
        combinedItems.push({
          id: comment.id,
          type: 'comment',
          data: comment,
          timestamp: comment.created_at
        });
      });
      
      // HIGHLIGHT: Sort in descending order (newest first)
      combinedItems.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      
      setTimelineItems(combinedItems);
    }
  }, [assigneeChanges, comments, isLoading, task]); // HIGHLIGHT: Added task dependency

  const loadAssigneeChanges = async () => {
    try {
      const changes = await taskAPI.getTaskAssigneeChanges(taskId);
      setAssigneeChanges(changes);
    } catch (error) {
      console.error('Failed to load assignee changes:', error);
      setAssigneeChanges([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getInitials = (name: string): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const formatRelativeTime = (dateString: string): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
    if (diffInDays < 7) return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // HIGHLIGHT: Added renderTaskCreate function
  const renderTaskCreate = (taskData: Task) => {
    const reporterName = taskData.reporter_name || 'Unknown User';
    const reporterInitials = getInitials(reporterName);

    return (
      <div className="flex items-start gap-3 py-2">
        {/* User Avatar */}
        <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Plus className="w-3 h-3 text-white" /> {/* HIGHLIGHT: Using Plus icon for creation */}
        </div>
        
        <div className="flex-1 min-w-0">
          {/* Main action line */}
          <div className="flex items-baseline gap-2">
            <span className="font-semibold text-gray-900 text-sm">{reporterName}</span>
            <span className="text-gray-600 text-sm">created the Work item</span>
            <span className="text-gray-400 text-xs">•</span>
            <span className="text-gray-500 text-xs">{formatRelativeTime(taskData.created_at)}</span>
          </div>
          
          {/* Task title */}
          <div className="mt-1 text-sm">
            <span className="text-gray-700 font-medium">{taskData.title}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderAssigneeChange = (change: AssigneeChange) => {
    const changedByName = change.changed_by_name || 'Unknown User';
    const changedByInitials = getInitials(changedByName);
    
    const oldAssigneeName = change.old_assignee_name || 'Unassigned';
    const newAssigneeName = change.new_assignee_name || 'Unknown User';

    return (
      <div className="flex items-start gap-3 py-2">
        {/* User Avatar */}
        <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-xs font-medium text-white">{changedByInitials}</span>
        </div>
        
        <div className="flex-1 min-w-0">
          {/* Main action line */}
          <div className="flex items-baseline gap-2">
            <span className="font-semibold text-gray-900 text-sm">{changedByName}</span>
            <span className="text-gray-600 text-sm">changed the Assignee</span>
            <span className="text-gray-400 text-xs">•</span>
            <span className="text-gray-500 text-xs">{formatRelativeTime(change.changed_at)}</span>
          </div>
          
          {/* Assignee change details */}
          <div className="flex items-center gap-2 mt-1 text-sm">
            <span className="text-gray-600">{oldAssigneeName}</span>
            <ArrowRight className="w-3 h-3 text-gray-500" />
            <span className="font-medium text-gray-900">{newAssigneeName}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderComment = (comment: Comment) => {
    const userName = comment.user_name || 'Unknown User';
    const userInitials = getInitials(userName);

    return (
      <div className="flex items-start gap-3 py-2">
        {/* User Avatar */}
        <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-xs font-medium text-white">{userInitials}</span>
        </div>
        
        <div className="flex-1 min-w-0">
          {/* Main action line */}
          <div className="flex items-baseline gap-2">
            <span className="font-semibold text-gray-900 text-sm">{userName}</span>
            <span className="text-gray-600 text-sm">commented</span>
            <span className="text-gray-400 text-xs">•</span>
            <span className="text-gray-500 text-xs">{formatRelativeTime(comment.created_at)}</span>
          </div>
          
          {/* Comment content */}
          <div className="mt-1 text-sm prose prose-sm max-w-none text-gray-700 rich-text-view">
            <div dangerouslySetInnerHTML={{ __html: processHtmlForDisplay((comment as any).content_html || comment.content || '') }} />
          </div>
        </div>
      </div>
    );
  };

  const renderTimelineItem = (item: TimelineItem) => {
    switch (item.type) {
      case 'task_create': // HIGHLIGHT: Added task_create case
        return renderTaskCreate(item.data as Task);
      case 'assignee_change':
        return renderAssigneeChange(item.data as AssigneeChange);
      case 'comment':
        return renderComment(item.data as Comment);
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {timelineItems.length > 0 ? (
        timelineItems.map((item) => (
          <div key={`${item.type}-${item.id}`}>
            {renderTimelineItem(item)}
          </div>
        ))
      ) : (
        <div className="text-center py-8 text-gray-500">
          <UserIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p>No activity found</p>
          <p className="text-sm mt-1">Assignee changes and comments will appear here</p>
        </div>
      )}
    </div>
  );
};